// Types and configuration constants for the Advanced Mode Wizard

export type WizardStep = {
  number: number;
  title: string;
  subtitle: string;
};

export type UploadCategory = 'bcpFramework' | 'bcpPlan' | 'businessImpact';

export type UploadedFileItem = {
  id: string;
  name: string;
  sizeLabel: string;
};

export type UploadSection = {
  key: UploadCategory;
  title: string;
  description: string;
  hint: string;
  filesLabel: string;
};

export type SelectableChipOption = {
  id: string;
  label: string;
};

export type ExerciseFormatOption = {
  id: string;
  title: string;
  description: string;
  icon: string;
};

export type ObjectiveOption = {
  id: string;
  label: string;
};

export type IntensityOption = {
  id: string;
  title: string;
  description: string;
};

export type GeneratedImagePreview = {
  id: string;
  title: string;
  moduleIndex?: number;
  mimeType: string;
  src: string;
  filename: string;
};

// Wizard Steps Configuration
export const WIZARD_STEPS: WizardStep[] = [
  { number: 1, title: 'Context', subtitle: 'Organization Profile' },
  { number: 2, title: 'BCP / BIA', subtitle: 'Continuity Frameworks' },
  { number: 3, title: 'Scope', subtitle: 'Participants & Format' },
  { number: 4, title: 'Objectives', subtitle: 'Exercise Goals' },
  { number: 5, title: 'Scenario', subtitle: 'Threat Generation' },
  { number: 6, title: 'Logistic', subtitle: 'Timing & Artifacts' },
  { number: 7, title: 'Final Output', subtitle: 'Generate & Export' },
];

// Duration Options
export const DURATION_OPTIONS: SelectableChipOption[] = [
  { id: '2-hours', label: '2 hours' },
  { id: '4-hours', label: '4 hours' },
  { id: '6-hours', label: '6 hours' },
  { id: '8-hours', label: '8 hours' },
  { id: 'full-day', label: 'Full day' },
  { id: 'multi-day', label: 'Multi-day' },
];

// Intensity Options
export const INTENSITY_OPTIONS: IntensityOption[] = [
  {
    id: 'low',
    title: 'Low',
    description: 'Introductory, minimal pressure.',
  },
  {
    id: 'medium',
    title: 'Medium',
    description: 'Balanced challenge.',
  },
  {
    id: 'high',
    title: 'High',
    description: 'Maximum pressure, cascading failures.',
  },
];

// Social Platform Options
export const SOCIAL_PLATFORM_OPTIONS: SelectableChipOption[] = [
  { id: 'twitter', label: 'Twitter' },
  { id: 'linkedin', label: 'Linkedin' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'reddit', label: 'Reddit' },
  { id: 'nextdoor', label: 'Nextdoor' },
];

// Upload Sections Configuration
export const UPLOAD_SECTIONS: UploadSection[] = [
  {
    key: 'bcpFramework',
    title: 'BCP Framework',
    description: 'Governance policy & overarching continuity program.',
    hint: 'Or drag and drop your BCP framework here.',
    filesLabel: 'BCP Framework files',
  },
  {
    key: 'bcpPlan',
    title: 'BCP Plan',
    description: 'Governance policy & overarching continuity program.',
    hint: 'Or drag and drop your BCP plan here.',
    filesLabel: 'BCP Plan files',
  },
  {
    key: 'businessImpact',
    title: 'Business Impact Analysis',
    description: 'RTO/RPO targets & critical function rankings.',
    hint: 'Or drag and drop your Business Impact Analysis here.',
    filesLabel: 'Business Impact files',
  },
];

// Participant Options
export const PARTICIPANT_OPTIONS: SelectableChipOption[] = [
  { id: 'executive-leadership', label: 'Executive Leadership (C-Suite)' },
  { id: 'it-cybersecurity', label: 'IT / Cybersecurity' },
  { id: 'operations-facilities', label: 'Operation / Facilities' },
  { id: 'legal-compliance', label: 'Legal/Compliance' },
  { id: 'communications-pr', label: 'Communications / PR' },
  { id: 'human-resources', label: 'Human Resources' },
  { id: 'finance', label: 'Finance' },
  { id: 'supply-chain-logistics', label: 'Supply Chain / Logistics' },
  { id: 'clinical-patient-care', label: 'Clinical / Patient Care' },
  { id: 'risk-management', label: 'Risk Management' },
  { id: 'external-partners', label: 'External Partners' },
  { id: 'board-of-directors', label: 'Board of Directors' },
];

// Exercise Format Options
export const EXERCISE_FORMATS: ExerciseFormatOption[] = [
  {
    id: 'tabletop-discussion',
    title: 'Tabletop Discussion',
    description: 'Discussion-based, no live action',
    icon: 'assets/icons/discussion_icon.svg',
  },
  {
    id: 'functional-drill',
    title: 'Functional Drill',
    description: 'Activates specific functions',
    icon: 'assets/icons/settings_icon.svg',
  },
  {
    id: 'full-scale-exercise',
    title: 'Full-Scale Exercise',
    description: 'Operational deployment',
    icon: 'assets/icons/exclamation_mark_icon.svg',
  },
  {
    id: 'workshop',
    title: 'Workshop',
    description: 'Education & capability building',
    icon: 'assets/icons/workshop_icon.svg',
  },
  {
    id: 'game-day',
    title: 'Game Day',
    description: 'Live system/process testing',
    icon: 'assets/icons/circle_icon.svg',
  },
  {
    id: 'seminar',
    title: 'Seminar',
    description: 'Awareness presentation',
    icon: 'assets/icons/book_icon.svg',
  },
];

// Threat Seed Options
export const THREAT_SEED_OPTIONS: SelectableChipOption[] = [
  { id: 'ransomware-attack', label: 'Ransomware Attack' },
  { id: 'severe-weather-flood', label: 'Severe Weather / Flood' },
  { id: 'power-grid-failure', label: 'Power Grid Failure' },
  { id: 'supply-chain-disruption', label: 'Supply Chain Disruption' },
  { id: 'disinformation-campaign', label: 'Disinformation Campaign' },
  { id: 'pandemic-health-crisis', label: 'Pandemic / Health Crisis' },
  { id: 'insider-threat', label: 'Insider Threat' },
  { id: 'telecom-outage', label: 'Telecom Outage' },
];

// Step Four Threat Seeds (duplicate for backward compatibility)
export const STEP_FOUR_THREAT_SEEDS: SelectableChipOption[] = [
  { id: 'ransomware-attack', label: 'Ransomware Attack' },
  { id: 'severe-weather-flood', label: 'Severe Weather / Flood' },
  { id: 'power-grid-failure', label: 'Power Grid Failure' },
  { id: 'supply-chain-disruption', label: 'Supply Chain Disruption' },
  { id: 'disinformation-campaign', label: 'Disinformation Campaign' },
  { id: 'pandemic-health-crisis', label: 'Pandemic / Health Crisis' },
  { id: 'insider-threat', label: 'Insider Threat' },
  { id: 'telecom-outage', label: 'Telecom Outage' },
];

// Step Four Objective Options
export const STEP_FOUR_OBJECTIVE_OPTIONS: SelectableChipOption[] = [
  {
    id: 'notification-protocols',
    label: 'Test notification and communication protocols',
  },
  {
    id: 'executive-decision-making',
    label: 'Evaluate executive decision-making under pressure',
  },
  {
    id: 'it-failover-recovery',
    label: 'Validate IT system failover and recovery procedures',
  },
  {
    id: 'supply-chain-response',
    label: 'Assess supply chain disruption response',
  },
  {
    id: 'employee-safety',
    label: 'Test employee safety and accountability processes',
  },
  {
    id: 'external-agencies',
    label: 'Evaluate coordination with external agencies',
  },
  {
    id: 'backup-data-recovery',
    label: 'Validate backup data recovery (RTO/RPO)',
  },
  {
    id: 'crisis-media-strategy',
    label: 'Test crisis communications and media strategy',
  },
  {
    id: 'legal-regulatory-compliance',
    label: 'Evaluate legal and regulatory compliance during crisis',
  },
  {
    id: 'financial-continuity',
    label: 'Assess financial continuity and emergency funding',
  },
  {
    id: 'cybersecurity-response',
    label: 'Assess cybersecurity incident response procedures',
  },
];

// Default Objective Options
export const DEFAULT_OBJECTIVE_OPTIONS: ObjectiveOption[] = [
  {
    id: 'test-notification-protocols',
    label: 'Test notification and communication protocols',
  },
  {
    id: 'evaluate-decision-making',
    label: 'Evaluate executive decision-making under pressure',
  },
  {
    id: 'validate-failover-recovery',
    label: 'Validate IT system failover and recovery procedures',
  },
  {
    id: 'assess-supply-chain-response',
    label: 'Assess supply chain disruption response',
  },
];
